/**
 * Helpers for recognizing image files during drag & drop uploads.
 *
 * Browsers don't always set `File.type` reliably:
 *   - iPhone/iPad HEIC/HEIF exports often arrive with type="" even
 *     though the content is a valid image.
 *   - Dragging from certain apps (Preview, Explorer, some camera
 *     syncs) also strips the MIME.
 *   - RAW formats (CR2, NEF, ARW) have no browser-registered MIME.
 *
 * Our old `(f.type || "").startsWith("image/")` filter was rejecting
 * these as "not an image", which is exactly the wrong default for a
 * field-ops app where crews drop photos straight off a phone backup.
 *
 * We now accept a file as an image when EITHER:
 *   - the MIME starts with `image/`, OR
 *   - the filename has a recognized photo extension (even if MIME
 *     is empty / wrong).
 *
 * We still exclude obvious non-image types (application/pdf, video/*,
 * text/*) so a mis-dropped PDF or video gets rejected with a clear
 * toast instead of being 400'd by the backend.
 */

import { isVideoFile } from "@/lib/videoUpload";

// Extensions that cover anything a telecom field crew would legitimately
// drop onto a photo slot. Kept conservative — adding tiff/raw later is
// trivial if a customer asks.
const PHOTO_EXTENSIONS = new Set([
  "jpg", "jpeg", "jpe", "jfif",
  "png",
  "heic", "heif",
  "webp",
  "gif",
  "bmp",
]);

function _extOf(name) {
  if (!name || typeof name !== "string") return "";
  const dot = name.lastIndexOf(".");
  if (dot < 0 || dot === name.length - 1) return "";
  return name.slice(dot + 1).toLowerCase();
}

/**
 * True when `file` is plausibly a photo. Tolerant of empty MIME
 * (HEIC from iPhone) by falling back to extension. Explicitly rejects
 * non-image MIME types to catch mis-drags of PDFs, videos, text.
 */
export function looksLikeImage(file) {
  if (!file) return false;
  const type = (file.type || "").toLowerCase();
  if (type.startsWith("image/")) return true;
  // Explicit non-image MIME → never an image, don't check extension
  // (prevents a .pdf renamed .jpg from sneaking through, and also
  // skips videos/text/pdf cleanly).
  if (type && !type.startsWith("image/")) return false;
  // Empty MIME → fall back to extension sniff.
  return PHOTO_EXTENSIONS.has(_extOf(file.name));
}

/**
 * Split a drop batch into images vs non-images. Returns both so the
 * caller can show a toast with the skipped count.
 */
export function partitionImages(files, { allowVideo = false } = {}) {
  const images = [];
  const skipped = [];
  for (const f of files) {
    if (looksLikeImage(f) || (allowVideo && isVideoFile(f))) images.push(f);
    else skipped.push(f);
  }
  return { images, skipped };
}


// In-app photo drags (thumbnail → another slot) carry this type instead of a file,
// so a move never re-uploads the low-res thumbnail the browser would otherwise hand over.
export const PHOTO_DRAG_TYPE = "application/x-f2-photo";

export const isInternalPhotoDrag = (e) => !!e.dataTransfer?.types?.includes(PHOTO_DRAG_TYPE);

export const isFileOrPhotoDrag = (e) =>
  isInternalPhotoDrag(e) || !!e.dataTransfer?.types?.includes("Files");

